// ─────────────────────────────────────────────────────────────────────────
// 익명 로그인 — 학생·강의자가 코드로 들어오는 유일한 자격이다.
//
// 실제로 난 사고: Firebase 콘솔에서 Anonymous 제공자가 꺼져 있었다. 로그인은 400
// (ADMIN_ONLY_OPERATION)으로 떨어졌는데 호출부가 그 오류를 통째로 삼켰고
// (`catch { /* 이미 로그인 상태면 무시 */ }`), 이어진 Firestore 읽기가
// "Missing or insufficient permissions" 로 실패했다. 학생에게는 **엉뚱한 메시지**만
// 보이고, 담당자도 화면만 보고는 원인을 알 수 없었다.
//
// 왜 안 걸렸나: 담당자 브라우저는 이미 콘솔 계정으로 로그인돼 있어서 isSignedIn() 을
// 통과했다. 다른 브라우저에서 학생으로 들어가 봐야만 드러난다.
//
// 규칙 3 — 없는 것과 안 본 것을 구분한다. 여기서는 **"코드가 틀렸다"와 "로그인을
// 못 했다"** 를 구분한다. 삼키면 앞의 것으로 보인다.
// 규칙 8 — 다시 해도 안 될 일에 "잠시 뒤 다시"라고 하지 않는다. 설정 문제는 영영
//   그대로라, 그렇게 적으면 학생은 끝없이 새로고침한다.
// ─────────────────────────────────────────────────────────────────────────

/** 다시 시도해도 소용없는 오류 — 프로젝트 설정을 고쳐야 풀린다. */
const CONFIG_CODES = new Set([
  'auth/operation-not-allowed',   // Anonymous 제공자 꺼짐 (ADMIN_ONLY_OPERATION)
  'auth/admin-restricted-operation',
  'auth/unauthorized-domain',     // 이 도메인이 Auth 허용 목록에 없음
  'auth/invalid-api-key',
  'auth/api-key-not-valid.-please-pass-a-valid-api-key.',
]);

export const ANON_AUTH_CONFIG_MESSAGE =
  '지금 이 서비스에 접속할 수 없습니다. 코드 문제가 아니니 운영자에게 알려주세요.';
export const ANON_AUTH_RETRY_MESSAGE =
  '서버에 연결하지 못했습니다. 네트워크를 확인하고 다시 시도해 주세요.';

/**
 * 익명 로그인을 보장한다. 이미 로그인돼 있으면 아무것도 하지 않는다.
 *
 * 실패하면 **던진다.** 삼키면 다음 줄의 권한 오류로 둔갑해 원인을 잃는다.
 * 진짜 이유는 콘솔에 남긴다 — 화면 문구로는 담당자가 무엇을 고쳐야 할지 모른다.
 *
 * @param {import('firebase/auth').Auth} auth
 * @param {{signInAnonymously: Function}} fbAuth  firebase/auth 모듈
 */
export async function ensureAnonAuth(auth, fbAuth) {
  if (!auth) throw new Error('서버 연결이 구성되지 않았습니다.');
  if (auth.currentUser) return auth.currentUser;
  try {
    const cred = await fbAuth.signInAnonymously(auth);
    return cred?.user || auth.currentUser;
  } catch (e) {
    const code = e?.code || '';
    // 담당자가 고칠 수 있게 진짜 이유를 남긴다. 화면 문구에는 이걸 싣지 않는다.
    // eslint-disable-next-line no-console
    console.error('[anonAuth] 익명 로그인 실패:', code || e?.message || e,
      '— Firebase 콘솔 > Authentication > Sign-in method 에서 Anonymous 가 켜져 있는지,'
      + ' 이 도메인이 승인된 도메인에 있는지 확인하세요.');
    const err = new Error(CONFIG_CODES.has(code) ? ANON_AUTH_CONFIG_MESSAGE : ANON_AUTH_RETRY_MESSAGE);
    err.code = code || 'auth/unknown';
    err.isConfig = CONFIG_CODES.has(code);
    throw err;
  }
}
